import { useState, useEffect, useRef, useCallback } from 'react';

const PULL_THRESHOLD = 72; // px to trigger refresh
const MAX_PULL = 120; // max px to pull before capping
const RESISTANCE = 2.5; // drag resistance factor

function isStandalonePWA(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>;
  containerRef?: React.RefObject<HTMLElement>;
}

interface UsePullToRefreshReturn {
  pullDistance: number;
  isRefreshing: boolean;
  isTriggered: boolean;
}

export function usePullToRefresh({
  onRefresh,
  containerRef,
}: UsePullToRefreshOptions): UsePullToRefreshReturn {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isTriggered, setIsTriggered] = useState(false);

  const startYRef = useRef<number | null>(null);
  const isPullingRef = useRef(false);
  const isRefreshingRef = useRef(false);
  // Track if scroll position was > 0 when touch started - prevents pull-to-refresh
  // from activating when user scrolls back to top from a scrolled position
  const touchStartedWhileScrolledRef = useRef(false);

  const getScrollTop = useCallback(() => {
    // Always use window scroll since PullToRefresh no longer creates its own scroll container
    return window.scrollY || document.documentElement.scrollTop || 0;
  }, []);

  useEffect(() => {
    // Only activate in standalone PWA mode
    if (!isStandalonePWA()) return;

    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      const scrollTop = getScrollTop();
      
      // Track whether we started this touch while scrolled
      touchStartedWhileScrolledRef.current = scrollTop > 2;
      
      // Only start pull if we're at the very top AND we weren't scrolled
      if (scrollTop <= 2) {
        startYRef.current = touch.clientY;
        isPullingRef.current = true;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isPullingRef.current || startYRef.current === null || isRefreshingRef.current) return;
      
      // If touch started while scrolled, never activate pull-to-refresh for this gesture
      if (touchStartedWhileScrolledRef.current) {
        isPullingRef.current = false;
        setPullDistance(0);
        setIsTriggered(false);
        return;
      }

      // Double-check scroll position - if we've scrolled since touch start, cancel pull
      const currentScrollTop = getScrollTop();
      if (currentScrollTop > 2) {
        isPullingRef.current = false;
        setPullDistance(0);
        setIsTriggered(false);
        return;
      }

      const touch = e.touches[0];
      const delta = touch.clientY - startYRef.current;

      if (delta <= 0) {
        setPullDistance(0);
        setIsTriggered(false);
        return;
      }

      // Prevent native scroll-bounce interfering
      if (currentScrollTop <= 2 && delta > 0) {
        e.preventDefault?.();
      }

      const capped = Math.min(delta / RESISTANCE, MAX_PULL);
      setPullDistance(capped);
      setIsTriggered(capped >= PULL_THRESHOLD);
    };

    const onTouchEnd = async () => {
      if (!isPullingRef.current) return;
      isPullingRef.current = false;

      if (pullDistance >= PULL_THRESHOLD && !isRefreshingRef.current) {
        isRefreshingRef.current = true;
        setIsRefreshing(true);
        setPullDistance(0);
        setIsTriggered(false);
        try {
          await onRefresh();
        } finally {
          isRefreshingRef.current = false;
          setIsRefreshing(false);
        }
      } else {
        setPullDistance(0);
        setIsTriggered(false);
      }

      startYRef.current = null;
      touchStartedWhileScrolledRef.current = false;
    };

    // Listen on window to avoid scroll container issues
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [onRefresh, getScrollTop, pullDistance]);

  return { pullDistance, isRefreshing, isTriggered };
}
